/**
 * Shared enablement answer for `useCommissionCostingEnabled`.
 *
 * That hook is per-call `useState`, not a context. Two mounts on the same
 * tenant used to start at `settings=null` / `enabled=false` independently, so
 * DestinationPage could pass its own gate while `useCommissionNavViewer`
 * still denied. Cache the resolved settings by tenant id, dedupe in-flight
 * fetches, and seed a newly mounted hook from what Sidebar already knows.
 *
 * Invalidated on scope change (same registry as PermissionContext) and when
 * `clearTenantSettingsCache` runs, so a Settings toggle cannot leave a stale
 * on/off answer.
 */
import { registerScopeCache } from '@/lib/scopeCaches';
import {
  isCommissionCostingEnabled,
  type TenantGateSettings,
} from '@/lib/tenantFeatureGates';

type CacheEntry = {
  settings: TenantGateSettings | null;
  resolved: boolean;
  inflight?: Promise<TenantGateSettings | null>;
};

const cache = new Map<string, CacheEntry>();

export function commissionCostingCacheKey(
  tenantId: string | undefined | null,
): string {
  return tenantId && tenantId.length > 0 ? tenantId : '__pending__';
}

export function peekCommissionCostingEnabledCache(
  tenantId: string | undefined | null,
): { settings: TenantGateSettings | null; resolved: boolean; enabled: boolean } | null {
  const entry = cache.get(commissionCostingCacheKey(tenantId));
  if (!entry) return null;
  return {
    settings: entry.settings,
    resolved: entry.resolved,
    enabled: isCommissionCostingEnabled(entry.settings),
  };
}

/**
 * Initial hook state for a tenant. A resolved cache entry seeds `enabled`
 * so a DestinationPage that mounts after Sidebar has fetched does not start
 * at `enabled=false`.
 */
export function initialCommissionCostingEnabledState(
  tenantId: string | undefined | null,
): { settings: TenantGateSettings | null; settingsLoading: boolean } {
  const peek = peekCommissionCostingEnabledCache(tenantId);
  if (peek?.resolved) {
    return { settings: peek.settings, settingsLoading: false };
  }
  return { settings: null, settingsLoading: true };
}

export function seedCommissionCostingEnabledCache(
  tenantId: string,
  settings: TenantGateSettings | null,
): void {
  cache.set(commissionCostingCacheKey(tenantId), { settings, resolved: true });
}

export function getOrStartCommissionCostingEnabledFetch(
  tenantId: string | undefined | null,
  fetchSettings: () => Promise<TenantGateSettings | null>,
): Promise<TenantGateSettings | null> {
  const key = commissionCostingCacheKey(tenantId);
  const existing = cache.get(key);
  if (existing?.inflight) return existing.inflight;

  const inflight = fetchSettings()
    .then((settings) => {
      cache.set(key, { settings, resolved: true });
      return settings;
    })
    .catch(() => {
      cache.set(key, { settings: null, resolved: true });
      return null;
    });

  cache.set(key, {
    settings: existing?.settings ?? null,
    resolved: existing?.resolved ?? false,
    inflight,
  });
  return inflight;
}

export function clearCommissionCostingEnabledCache(tenantId?: string): void {
  if (tenantId) {
    cache.delete(commissionCostingCacheKey(tenantId));
  } else {
    cache.clear();
  }
}

registerScopeCache('commissionCostingEnabledCache', () => cache.clear());
